import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
import { toast } from 'react-toastify'

import FilterSearch from 'pages/parts/FilterSearch'
import FilterPagination from 'pages/parts/FilterPagination'

import { httpOk, GET, POST } from 'helpers/api'
import { formatMoney, formatDate, regexPhoneNumber } from 'helpers/helpers'

const num = (v) => Number(String(v ?? '').replace(/\D/g, '')) || 0
const fmt = (v) => { const d = String(v ?? '').replace(/\D/g, ''); return d.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') }

// Oddiy harakatlar (KPI'дан tashqari)
const SIMPLE = {
	accrued: { labelKey: 'salary_word', titleKey: 'salary_calc', cls: 'btn-outline-success', icon: 'uil-money-bill' },
	bonus: { labelKey: 'bonus_word', titleKey: 'bonus_give', cls: 'btn-outline-primary', icon: 'uil-gift' },
	fine: { labelKey: 'fine_word', titleKey: 'fine_apply', cls: 'btn-outline-danger', icon: 'uil-minus-circle' },
}

function Kpi() {
	const { t } = useTranslation()

	const [data, setData] = useState([])
	const [selected, setSelected] = useState({})
	const [filterData, setFilterData] = useState({ search: '' })
	const [pagination, setPagination] = useState({ url: '/employee-account', response: null })

	// KPI modal (metr × mahsulot)
	const [showKpi, setShowKpi] = useState(false)
	const [rows, setRows] = useState([{ product: '', metr: '', rate: '' }])
	const [kpiNote, setKpiNote] = useState('')

	// Oddiy modal (hisoblash/bonus/jarima)
	const [simple, setSimple] = useState({ show: false, type: 'accrued', amount: '', note: '' })

	// Tarix modal (KPI / Oylik / Jarima)
	const HIST_TITLE = { kpi: t('calculated_kpi'), accrued: t('salary_calculated'), fine: t('fine_word') }
	const [history, setHistory] = useState({ show: false, name: '', type: 'kpi', items: [] })

	async function openHistory(emp, type) {
		const res = await GET('/employee-account/detail', { employee_id: emp.id }, { loader: true })
		if (httpOk(res)) {
			setHistory({ show: true, name: emp.name, type, items: (res.data.transactions || []).filter(x => x.type === type) })
		}
	}

	async function search() {
		var p = ''
		if (filterData.search) p += (p ? '&' : '?') + 'search=' + filterData.search
		if (filterData.page) p += (p ? '&' : '?') + 'page=' + filterData.page
		if (filterData.size) p += (p ? '&' : '?') + 'size=' + filterData.size
		const res = await GET('/employee-account' + p, {}, { loader: true })
		if (httpOk(res)) { setData(res.data); setPagination({ ...pagination, response: res.data }) }
	}

	function openKpi(emp) {
		setSelected(emp)
		setRows([{ product: '', metr: '', rate: '' }])
		setKpiNote('')
		setShowKpi(true)
	}
	function openSimple(emp, type) {
		setSelected(emp)
		setSimple({ show: true, type, amount: '', note: '' })
	}

	const kpiTotal = rows.reduce((s, r) => s + num(r.metr) * num(r.rate), 0)

	async function saveKpi(e) {
		e.preventDefault()
		if (kpiTotal <= 0) { toast.error(t('enter_amount')); return }
		const breakdown = rows.filter(r => num(r.metr) && num(r.rate))
			.map(r => `${r.product || 'mahsulot'}: ${fmt(r.metr)}m × ${fmt(r.rate)}`).join('; ')
		const note = [breakdown, kpiNote].filter(Boolean).join(' — ')
		const res = await POST('/employee-account', {
			employee_id: selected.id, type: 'kpi', amount: kpiTotal, note,
		}, { loader: true })
		if (httpOk(res)) {
			setShowKpi(false)
			search()
			toast.success(t('kpi_saved'))
		} else {
			toast.error(res?.data?.message || t('error_word'))
		}
	}

	async function saveSimple(e) {
		e.preventDefault()
		if (num(simple.amount) <= 0) { toast.error(t('enter_amount')); return }
		const res = await POST('/employee-account', {
			employee_id: selected.id, type: simple.type, amount: num(simple.amount), note: simple.note,
		}, { loader: true })
		if (httpOk(res)) {
			setSimple({ ...simple, show: false })
			search()
			toast.success(t('saved_ledger'))
		} else {
			toast.error(res?.data?.message || t('error_rejected'))
		}
	}

	function paginate(page, size) { setFilterData({ ...filterData, page, size }) }

	useEffect(() => { search() }, [filterData.search, filterData.page, filterData.size]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center">{t('kpi_calc')}</h4>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex mb-3">
						<FilterSearch filterData={filterData} setFilterData={setFilterData} />
					</div>

					<div className="table-responsive">
						<table className="table table-bordered mb-0 align-middle">
							<thead>
								<tr>
									<th className="text-center" style={{ minWidth: '33px' }}>#</th>
									<th>{t('name2')}</th>
									<th>{t('phone')}</th>
									<th className="text-end">{t('calculated_kpi')}</th>
									<th className="text-end">{t('salary_word')}</th>
									<th className="text-end">{t('fine_word')}</th>
									<th className="text-center" style={{ minWidth: '360px' }}>{t('kpi_actions')}</th>
								</tr>
							</thead>
							<tbody>
								{data?.data?.map((item, i) => {
									return (
										<tr key={item.id} className="tr-middle">
											<td className="text-center">{item.row_number}</td>
											<td className="fw-semibold">{item.name}</td>
											<td className="text-nowrap">{regexPhoneNumber(item.phone)}</td>
											<td className="text-end fw-bold text-info cursor" title={t('view_history')}
												onClick={() => openHistory(item, 'kpi')}>
												{formatMoney(item.kpi_total)} <i className="uil-history" style={{ fontSize: 13 }}></i>
											</td>
											<td className="text-end fw-bold text-success cursor" title={t('salary_history')}
												onClick={() => openHistory(item, 'accrued')}>
												{formatMoney(item.accrued_total)} <i className="uil-history" style={{ fontSize: 13 }}></i>
											</td>
											<td className="text-end fw-bold text-danger cursor" title={t('fine_history')}
												onClick={() => openHistory(item, 'fine')}>
												{formatMoney(item.fine_total)} <i className="uil-history" style={{ fontSize: 13 }}></i>
											</td>
											<td>
												<div className="d-flex gap-1 justify-content-center flex-wrap">
													<button className="btn btn-sm btn-primary" onClick={() => openKpi(item)}>
														<i className="uil-ruler me-1"></i>KPI (metr)
													</button>
													{Object.entries(SIMPLE).map(([type, cfg]) => (
														<button key={type} className={`btn btn-sm ${cfg.cls}`} title={t(cfg.titleKey)}
															onClick={() => openSimple(item, type)}>
															<i className={`${cfg.icon} me-1`}></i>{t(cfg.labelKey)}
														</button>
													))}
												</div>
											</td>
										</tr>
									)
								})}
								{(!data?.data || data.data.length === 0) &&
									<tr><td colSpan={7} className="text-center text-muted py-3">{t('no_employee')}</td></tr>
								}
							</tbody>
						</table>
					</div>

					{pagination.response && <FilterPagination pagination={pagination} paginate={paginate} />}
				</div>
			</div>

			{/* KPI (metr × mahsulot) MODAL */}
			<Modal show={showKpi} animation={false} centered size="lg" backdrop="static" onHide={() => setShowKpi(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('kpi_calc')} — {selected.name}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<form onSubmit={saveKpi} autoComplete="off">
						<div className="mb-2 small text-muted">{t('kpi_metr_hint')}</div>
						<div className="table-responsive">
							<table className="table table-sm table-bordered mb-2">
								<thead>
									<tr>
										<th>{t('product_word')}</th>
										<th style={{ width: '20%' }}>{t('metr_word')}</th>
										<th style={{ width: '22%' }}>{t('sum_per_metr')}</th>
										<th className="text-end" style={{ width: '20%' }}>{t('amount_word')}</th>
										<th style={{ width: '36px' }}></th>
									</tr>
								</thead>
								<tbody>
									{rows.map((r, i) => (
										<tr key={i}>
											<td>
												<input type="text" className="form-control form-control-sm" placeholder="Profnastil"
													value={r.product} onChange={(e) => { const c = [...rows]; c[i].product = e.target.value; setRows(c) }} />
											</td>
											<td>
												<input type="text" className="form-control form-control-sm text-end" inputMode="numeric"
													value={fmt(r.metr)} onChange={(e) => { const c = [...rows]; c[i].metr = e.target.value.replace(/\D/g, ''); setRows(c) }} />
											</td>
											<td>
												<input type="text" className="form-control form-control-sm text-end" inputMode="numeric"
													value={fmt(r.rate)} onChange={(e) => { const c = [...rows]; c[i].rate = e.target.value.replace(/\D/g, ''); setRows(c) }} />
											</td>
											<td className="text-end fw-semibold">{formatMoney(num(r.metr) * num(r.rate))}</td>
											<td className="text-center">
												{rows.length > 1 &&
													<i className="uil-times text-danger cursor" onClick={() => setRows(rows.filter((_, x) => x !== i))} />}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
						<button type="button" className="btn btn-outline-secondary btn-sm mb-3"
							onClick={() => setRows([...rows, { product: '', metr: '', rate: '' }])}>
							<i className="uil-plus me-1"></i>{t('add_product')}
						</button>

						<div className="d-flex justify-content-between align-items-center mb-3 p-2 rounded" style={{ background: '#f8f9fa' }}>
							<span className="fw-semibold">{t('total_kpi')}:</span>
							<span className="fs-5 fw-bold text-success">{formatMoney(kpiTotal)} so'm</span>
						</div>

						<div className="form-group">
							<label>{t('note')}</label>
							<input type="text" className="form-control" value={kpiNote} onChange={(e) => setKpiNote(e.target.value)} />
						</div>

						<div className="d-flex gap-2 mt-2">
							<button type="button" className="btn btn-outline-warning w-100" onClick={() => setShowKpi(false)}>{t('cancel')}</button>
							<button type="submit" className="btn btn-primary w-100">{t('save_to_ledger')}</button>
						</div>
					</form>
				</Modal.Body>
			</Modal>

			{/* HISOBLANGAN KPI TARIXI */}
			<Modal show={history.show} animation={false} centered size="lg" backdrop="static"
				onHide={() => setHistory({ ...history, show: false })}>
				<Modal.Header closeButton>
					<Modal.Title>{HIST_TITLE[history.type] || t('history_word')} — {history.name}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{history.items.length > 0 ? (
						<div className="table-responsive">
							<table className="table table-bordered table-sm mb-0">
								<thead>
									<tr>
										<th style={{ width: '18%' }}>{t('date')}</th>
										<th>{t('what_for_note')}</th>
										<th className="text-end" style={{ width: '20%' }}>{t('amount_word')}</th>
									</tr>
								</thead>
								<tbody>
									{history.items.map((tx, i) => (
										<tr key={i}>
											<td className="text-nowrap">{formatDate(tx.trx_date, 'dd.MM.yyyy')}</td>
											<td>{tx.note || '—'}</td>
											<td className={`text-end fw-semibold ${history.type === 'fine' ? 'text-danger' : 'text-success'}`}>{formatMoney(tx.amount)}</td>
										</tr>
									))}
								</tbody>
								<tfoot>
									<tr className="fw-bold">
										<td colSpan={2} className="text-end">{t('total_colon')}:</td>
										<td className={`text-end ${history.type === 'fine' ? 'text-danger' : 'text-success'}`}>{formatMoney(history.items.reduce((s, x) => s + Number(x.amount), 0))}</td>
									</tr>
								</tfoot>
							</table>
						</div>
					) : (
						<p className="text-muted small text-center py-3">{t('no_records_yet')}</p>
					)}
				</Modal.Body>
			</Modal>

			{/* ODDIY (hisoblash/bonus/jarima) MODAL */}
			<Modal show={simple.show} animation={false} centered backdrop="static" onHide={() => setSimple({ ...simple, show: false })}>
				<Modal.Header closeButton>
					<Modal.Title>{t(SIMPLE[simple.type]?.titleKey)} — {selected.name}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<form onSubmit={saveSimple} autoComplete="off">
						<div className="form-group">
							<label>{t('amount_sum')}<span className="required-mark">*</span></label>
							<input type="text" className="form-control" autoFocus inputMode="numeric"
								value={fmt(simple.amount)} onChange={(e) => setSimple({ ...simple, amount: e.target.value.replace(/\D/g, '') })} required />
						</div>
						<div className="form-group mb-0">
							<label>{t('note')}</label>
							<input type="text" className="form-control" value={simple.note}
								onChange={(e) => setSimple({ ...simple, note: e.target.value })} />
						</div>
						<div className="d-flex gap-2 mt-3">
							<button type="button" className="btn btn-outline-warning w-100" onClick={() => setSimple({ ...simple, show: false })}>{t('cancel')}</button>
							<button type="submit" className="btn btn-primary w-100">{t('save')}</button>
						</div>
					</form>
				</Modal.Body>
			</Modal>
		</>
	)
}

export default Kpi
